import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** Matches apps/api's `TokenResponse` exactly (auth.controller.ts). */
export interface TokenResponse {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly refreshExpiresAt: string;
  /** The password that opened this session is a provisional one: the only thing the account may do is choose its own. */
  readonly mustChangePassword: boolean;
}

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

export function useLoginMutation(): UseMutationResult<TokenResponse, ApiError, LoginInput> {
  return useMutation({
    mutationFn: (input: LoginInput) =>
      apiRequest<TokenResponse>('/auth/login', { method: 'POST', body: input }),
  });
}

export interface ChangePasswordInput {
  readonly currentPassword: string;
  readonly newPassword: string;
}

/**
 * `POST /auth/password/change` - the way out of a provisional password, and an ordinary change. Returns a FRESH session (every earlier
 * one has ended). The variables carry both passwords: the mutation is collected at once (`gcTime: 0`) and the page resets it.
 */
export function useChangePasswordMutation(): UseMutationResult<
  TokenResponse,
  ApiError,
  ChangePasswordInput
> {
  const { accessToken } = useSession();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: ChangePasswordInput) =>
      apiRequest<TokenResponse>('/auth/password/change', {
        method: 'POST',
        body: input,
        accessToken,
      }),
  });
}

/**
 * `POST /auth/password/forgot` always answers 202, known address or not
 * (auth.controller.ts's own comment: "the same reasoning as registration").
 * The caller must never render a different outcome for the two cases —
 * doing so would reopen the account-enumeration channel this response
 * shape exists to close.
 */
export function useForgotPasswordMutation(): UseMutationResult<
  { status: string },
  ApiError,
  { email: string; locale?: string }
> {
  return useMutation({
    mutationFn: (input: { email: string; locale?: string }) =>
      apiRequest<{ status: string }>('/auth/password/forgot', { method: 'POST', body: input }),
  });
}

/**
 * `POST /auth/password/reset` answers 204 on success. Unlike `forgot`, a
 * failure here (400, "invalid or expired") is meant to be shown verbatim —
 * the caller already holds a secret token, so the asymmetry cannot be used
 * to enumerate anything (auth.controller.ts's own comment on this route).
 */
export function useResetPasswordMutation(): UseMutationResult<
  null,
  ApiError,
  { token: string; password: string }
> {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      apiRequest<null>('/auth/password/reset', { method: 'POST', body: input }),
  });
}
